import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import styles from './AddPrimaryContact.module.scss';
import LDAPSearchField from '../shared/LDAPSearch/LDAPSearchField';

// Define contact interface
interface Contact {
  role: string;
  name: string;
  uid?: string;
  email?: string;
  department?: string;
  imageUrl?: string;
  type?: string;
}

interface AddPrimaryContactProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Contact | Contact[]) => void; // Updated to accept an array of contacts
  ldapContacts?: any[];
  existingContacts?: any[]; // Add prop for existing primary contacts for duplicate prevention
}

// Mock function to simulate LDAP search with the provided contacts
const mockLDAPSearch = (searchTerm: string, ldapContacts: any[]) => {
  return new Promise<any[]>(resolve => {
    // Simulate network delay
    setTimeout(() => {
      if (!searchTerm || searchTerm.length < 3) {
        resolve([]);
        return;
      }

      // Filter contacts that match the search term
      const results = ldapContacts.filter(
        contact =>
          contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (contact.email &&
            contact.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      resolve(results);
    }, 300);
  });
};

const AddPrimaryContact: React.FC<AddPrimaryContactProps> = ({
  isOpen,
  onClose,
  onSave,
  ldapContacts = [],
  existingContacts = []
}) => {
  // State for the form
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]); // Changed to array
  // Error state for validation
  const [validationError, setValidationError] = useState<string>('');

  // State for dropdowns
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState<boolean>(false);

  // Refs for handling outside clicks
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // Role options
  const roles = [
    { id: 'pmm', name: 'PROD MRKT MANAGER' },
    { id: 'pm', name: 'PRODUCT MANAGER' },
    { id: 'pgm', name: 'PROGRAM MANAGER' }
  ];

  // Handle outside clicks to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset form when modal is opened or closed
  useEffect(() => {
    if (isOpen) {
      setSelectedRole('');
      setSelectedContacts([]);
      setValidationError(''); // Clear error when opening
    }
  }, [isOpen]);

  // Track existing contacts separately from newly added ones
  const [existingRoleContacts, setExistingRoleContacts] = useState<any[]>([]);

  // Populate existing contacts whenever role changes (similar to DACI popup)
  useEffect(() => {
    if (isOpen && selectedRole) {
      // Load all existing contacts for the selected Primary Contact role as display chips
      // These are for display context only and should not trigger API calls when saved
      const existing = existingContacts
        .filter(contact => contact.role === selectedRole)
        .map(contact => ({
          name: contact.name,
          uid: contact.uid,
          email: contact.email || contact.mail || '',
          imageUrl: contact.imageUrl,
          type: 'ldap',
          isExisting: true, // Mark as existing to avoid re-saving
          id: contact.id // Preserve the ID if it exists
        }));

      setExistingRoleContacts(existing);

      // Initialize selectedContacts with existing contacts for display,
      // but we'll filter them out when saving (only save NEW contacts)
      setSelectedContacts(existing);
    } else if (isOpen && !selectedRole) {
      // Clear contacts if no role is selected
      setExistingRoleContacts([]);
      setSelectedContacts([]);
    }
  }, [isOpen, selectedRole, existingContacts]);

  // Add a new effect to handle the clean-up when closing
  const handleClose = () => {
    // Clear selected contacts and role
    setSelectedRole('');
    setSelectedContacts([]);
    setExistingRoleContacts([]);
    // Call the provided onClose
    onClose();
  };

  // Handle role selection
  const handleRoleSelect = (id: string, name: string) => {
    setSelectedRole(name);
    setIsRoleDropdownOpen(false);
  };

  // Handle contact selection
  const handleContactChange = (value: any) => {
    if (Array.isArray(value)) {
      setSelectedContacts(value);
    } else if (value && (value.uid || value.name)) {
      setSelectedContacts([value]);
    } else {
      setSelectedContacts([]);
    }
  };

  // Handle deletion of contacts (when user clicks X on a chip)
  // Just remove from the UI - the diff will be computed on save
  const handleDeleteExistingContact = (contactToDelete: any) => {
    // Remove from selected contacts immediately (UI update)
    setSelectedContacts(prev =>
      prev.filter(
        contact =>
          !(
            contact.uid === contactToDelete.uid &&
            contact.name === contactToDelete.name
          )
      )
    );

    // Also remove from existing role contacts to prevent re-adding
    setExistingRoleContacts(prev =>
      prev.filter(
        contact =>
          !(
            contact.uid === contactToDelete.uid &&
            contact.name === contactToDelete.name
          )
      )
    );
  };

  // Handle save button click
  // Use the same approach as Edit Primary Contacts - send the complete current list
  // The manager's replaceRoleContacts will compute the diff and handle adds/deletes
  const handleSave = () => {
    if (!selectedRole) {
      setValidationError('Please select a role before saving.');
      return;
    }

    // VALIDATION: Check if any contacts are selected for the current role
    if (selectedContacts.length === 0) {
      setValidationError('Please add at least one contact before saving.');
      console.error(
        '❌ AddPrimaryContact: Validation failed - no contacts selected for current role'
      );
      return;
    }

    // VALIDATION: Check if this would result in zero total contacts
    // Calculate total contacts after this save
    const contactsInOtherRoles = existingContacts.filter(
      c => c.role !== selectedRole
    );
    const totalContactsAfterSave =
      contactsInOtherRoles.length + selectedContacts.length;

    if (totalContactsAfterSave === 0) {
      setValidationError(
        'At least one primary contact is required. Please add a contact before saving.'
      );
      console.error(
        '❌ AddPrimaryContact: Validation failed - would result in zero contacts'
      );
      return;
    }

    // Get the original contacts for this role from existingContacts
    const originalContacts = existingContacts.filter(
      c => c.role === selectedRole
    );

    // Check if there are any changes
    const hasChanges =
      selectedContacts.length !== originalContacts.length ||
      selectedContacts.some(
        sc =>
          !originalContacts.some(oc => oc.uid === sc.uid && oc.name === sc.name)
      ) ||
      originalContacts.some(
        oc =>
          !selectedContacts.some(sc => sc.uid === oc.uid && sc.name === oc.name)
      );

    if (!hasChanges) {
      handleClose();
      return;
    }

    // Clear any existing error
    setValidationError('');

    // Normalize ALL contacts (not just new ones) - handle different API response formats
    const normalizedContacts = selectedContacts.map(contact => {
      // Normalize the contact data to handle different field names from LDAP API
      return {
        id: contact.id, // Preserve ID for existing contacts
        name: contact.name || contact.displayName || contact.fullName || '',
        uid: contact.uid || contact.username || contact.userId || '',
        email: contact.email || contact.mail || contact.emailAddress || '',
        department: contact.department || contact.dept || '',
        role: selectedRole,
        type: 'ldap',
        imageUrl: contact.uid
          ? `https://s7d2.scene7.com/is/image/IMGDIR/${contact.uid}`
          : 'https://via.placeholder.com/32'
      };
    });

    const invalidContact = normalizedContacts.find(
      contact =>
        !contact.name ||
        contact.name.trim() === '' ||
        !contact.uid ||
        contact.uid.trim() === '' ||
        !contact.email ||
        contact.email.trim() === ''
    );

    if (invalidContact) {
      console.error(
        '❌ AddPrimaryContact: Invalid contact data found:',
        invalidContact
      );
      return;
    }

    // Call onSave with the complete list for the role
    // The parent component will use replaceRoleContacts which computes the diff
    try {
      onSave(normalizedContacts);
      handleClose();
    } catch (error) {
      console.error('AddPrimaryContact: Error in onSave call:', error);
    }
  };

  // Only render when open
  if (!isOpen) return null;

  // DEBUGGING: Check component state

  // Create portal to render at the top level of the DOM
  return createPortal(
    <div className={styles.overlay} data-testid="add-primary-contact-overlay">
      <div
        className={styles.modalContainer}
        data-testid="add-primary-contact-modal"
      >
        <div
          className={styles.addPrimaryContactContainer}
          data-testid="add-primary-contact-container"
        >
          {/* Header */}
          <div
            className={styles.header}
            data-testid="add-primary-contact-header"
          >
            <div className={styles.headerText}>
              <h2 className={styles.title}>Add Primary Contact</h2>
            </div>
          </div>

          {/* Form */}
          <div
            className={styles.formContainer}
            data-testid="add-primary-contact-form"
          >
            {/* Role Dropdown */}
            <div
              className={styles.formGroup}
              data-testid="add-primary-contact-role-group"
            >
              <label className={styles.label}>CONTACT ROLE</label>
              <div
                className={styles.dropdownContainer}
                ref={roleDropdownRef}
                data-testid="add-primary-contact-role-dropdown"
              >
                <div
                  className={`${styles.pickerContainer} ${styles.centeredPickerContainer}`}
                  onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                  data-testid="add-primary-contact-role-picker"
                >
                  <div className={styles.valueFrame}>
                    <div
                      className={styles.value}
                      data-testid="add-primary-contact-role-value"
                    >
                      {selectedRole || 'Select Role'}
                    </div>
                  </div>
                  <div className={styles.chevronContainer}>
                    <ChevronDown size="M" aria-hidden="true" />
                  </div>
                </div>

                {isRoleDropdownOpen && (
                  <div
                    className={styles.dropdown}
                    data-testid="add-primary-contact-role-options"
                  >
                    {roles.map(role => (
                      <div
                        key={role.id}
                        className={`${styles.dropdownItem} ${
                          selectedRole === role.name ? styles.selected : ''
                        }`}
                        onClick={() => handleRoleSelect(role.id, role.name)}
                        data-testid={`add-primary-contact-role-option-${role.id}`}
                      >
                        {role.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Contact LDAP Search Field */}
            <div
              className={`${styles.formGroup} ${
                validationError ? styles.hasError : ''
              }`}
              data-testid="add-primary-contact-name-group"
            >
              <label className={styles.label}>CONTACT NAME *</label>
              <div
                className={styles.ldapSearchContainer}
                data-testid="add-primary-contact-ldap-container"
              >
                <LDAPSearchField
                  label=""
                  value={
                    selectedContacts.length === 1
                      ? selectedContacts[0]
                      : selectedContacts
                  }
                  onChange={handleContactChange}
                  isRequired={false}
                  existingContacts={selectedContacts} // Pass current selectedContacts to reflect deletions
                  currentDaciRole={selectedRole} // Use selected role as current role for duplicate checking
                  onDeleteExistingContact={handleDeleteExistingContact} // Handle deletion of existing contacts
                  cssClasses={{
                    selectedTag: styles.selectedTag,
                    tagContainer: styles.tagContainer
                  }}
                />
              </div>

              {/* Validation Error Message */}
              {validationError && (
                <div
                  className={styles.errorMessage}
                  data-testid="add-primary-contact-error"
                >
                  {validationError}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className={styles.actionsContainer}
            data-testid="add-primary-contact-actions"
          >
            <button
              className={styles.cancelButton}
              onClick={handleClose}
              data-testid="add-primary-contact-cancel-button"
            >
              <span
                className={`${styles.buttonText} ${styles.cancelButtonText}`}
              >
                Cancel
              </span>
            </button>
            <button
              className={styles.saveButton}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
              disabled={!selectedRole}
              data-testid="add-primary-contact-save-button"
            >
              <span className={`${styles.buttonText} ${styles.saveButtonText}`}>
                Save
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddPrimaryContact;
